import { getReversedDashedDateUS } from 'utils/stringutils';

import { MergedDetail } from '../types';

export type SetValue = (value?: MergedDetail['value']) => void;
type SetIsDirty = (value: boolean) => void;
type ChangeEvent = React.ChangeEvent<HTMLInputElement>;

const onAvailabilityChange =
  (setValue: SetValue, value: MergedDetail['value'], setIsDirty: SetIsDirty) =>
  (event: ChangeEvent) => {
    if (setIsDirty) {
      setIsDirty(true);
    }
    const selectedAvailability = event.target.value;
    if (Array.isArray(value)) {
      const selectedOptionsCopy = value.slice();
      if (value.includes(selectedAvailability)) {
        const newValue = selectedOptionsCopy.filter(
          option => option !== selectedAvailability,
        );
        setValue(newValue);
      } else {
        selectedOptionsCopy.push(selectedAvailability);
        setValue(selectedOptionsCopy);
      }
    }
  };

const onDateChange =
  (setValue: SetValue, setIsDirty: SetIsDirty) =>
  (date: Date | Date[] | string | undefined) => {
    if (setIsDirty) {
      setIsDirty(true);
    }
    // The Datepicker's clear button fires undefined. DataValidationModal's
    // `mergeWith` and JSON.stringify both drop undefined, which leaves the
    // previous date in the payload. null survives both, and the info
    // endpoints treat a blank value as a clear.
    if (!date) {
      setValue(null);
      return;
    }
    setValue(getReversedDashedDateUS(date) || null);
  };

// Applicant data written by an import or the API can hold a checkboxes
// field as a comma-delimited string, which Applicant::Update splits the
// same way, or hold numbers. Checkbox inputs report strings, so every
// stored value is compared as one. An option value can itself contain
// commas, so a stored string that already matches an option value is
// kept whole. A stored entry that reads as a trimmed comma-segment of a
// current option value is a fragment left over from an earlier split and
// gets dropped; every other unmatched entry (a renamed or removed
// option, a case or spacing drift) is kept so the response survives.
const toCheckboxValues = (
  value: MergedDetail['value'],
  optionValues?: ReadonlyArray<number | string>,
): string[] => {
  const optionSet = new Set((optionValues ?? []).map(String));
  const hasOptions = optionSet.size > 0;
  const fragmentSet = new Set<string>();
  if (hasOptions) {
    optionSet.forEach(opt => {
      if (!opt.includes(',')) return;
      opt
        .split(',')
        .map(part => part.trim())
        .forEach(part => {
          if (part !== '' && !optionSet.has(part)) fragmentSet.add(part);
        });
    });
  }
  const pieces = (Array.isArray(value) ? value : [value]).flatMap(el => {
    if (typeof el === 'string') {
      if (hasOptions && optionSet.has(el)) return [el];
      return el.split(',').map(part => part.trim());
    }
    return [el];
  });
  const normalized = pieces
    .filter(el => (typeof el === 'string' ? el !== '' : typeof el === 'number'))
    .map(String);
  const kept = hasOptions
    ? normalized.filter(el => optionSet.has(el) || !fragmentSet.has(el))
    : normalized;
  return Array.from(new Set(kept));
};

const onCheckboxChange =
  (
    value: MergedDetail['value'],
    setValue: SetValue,
    setIsDirty: SetIsDirty,
    optionValues?: ReadonlyArray<number | string>,
  ) =>
  (event: ChangeEvent) => {
    if (setIsDirty) {
      setIsDirty(true);
    }
    const clickedBox = event.target.value;
    const values = toCheckboxValues(value, optionValues);
    if (values.includes(clickedBox)) {
      setValue(values.filter(el => el !== clickedBox));
    } else {
      setValue(values.concat(clickedBox));
    }
  };

const onDropdownChange =
  (setValue: SetValue, setIsDirty: SetIsDirty) => (selectedValue: string) => {
    if (setIsDirty) {
      setIsDirty(true);
    }
    setValue(selectedValue);
  };

const onAddressChange = (
  newValue: MergedDetail['value'],
  setValue: SetValue,
  setIsDirty: SetIsDirty,
) => {
  if (setIsDirty) {
    setIsDirty(true);
  }
  setValue(newValue);
};

const onValueChange =
  (setValue: SetValue, setIsDirty: SetIsDirty) => (event: ChangeEvent) => {
    if (setIsDirty) {
      setIsDirty(true);
    }
    setValue(event.target.value);
  };

const formatValue = (
  detail: MergedDetail,
  value: MergedDetail['value'],
): MergedDetail['value'] => {
  if (Array.isArray(value) || detail.type === 'address') {
    return value;
  }
  if (value && typeof value === 'object' && 'value' in value) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return value.value;
  }
  return value;
};

export {
  formatValue,
  onAddressChange,
  onAvailabilityChange,
  onCheckboxChange,
  onDateChange,
  onDropdownChange,
  onValueChange,
  toCheckboxValues,
};
